/**
 * "Näytöt ja ajoitus" for one chain module in /admin/workshop (#683): duration,
 * the deck's slide range and participant access, saved at once through
 * API_ENDPOINTS.INSTANCE_CHAIN_STEP. The module's screen states are
 * edited on Näyttösisältö (/admin/screen-content); this panel links there.
 */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, Monitor } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { media } from '../../styles/breakpoints';

const Wrap = styled.div`
  margin: 0 0 20px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
`;

const Toggle = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 0 16px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  svg { transition: transform 0.2s; transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')}); }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 18px;
  padding: 4px 16px 16px;
  ${media.sm} { grid-template-columns: minmax(0, 1fr); }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  input, select {
    min-height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(0, 0, 0, 0.25);
    color: #fff;
    font-size: 14px;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  padding: 0 16px 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
`;

const Status = styled.span`
  color: ${(p) => (p.$error ? '#f87171' : 'rgba(134, 239, 172, 0.9)')};
`;

const toInt = (v) => (v === '' || v == null ? null : Number.parseInt(v, 10));

/**
 * @param {{ step: object, locked?: boolean, hasDeck?: boolean, defaultOpen?: boolean,
 *   onSave: (patch: object) => Promise<unknown>, onEditScreens?: () => void }} props
 */
export default function ModuleSettingsPanel({
  step, locked = false, hasDeck = false, defaultOpen = false, onSave, onEditScreens,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState({});
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setDraft({
      duration: step.suggestedDuration ?? '',
      slideStart: step.slideStart ?? '',
      slideEnd: step.slideEnd ?? '',
    });
  }, [step.stepId, step.suggestedDuration, step.slideStart, step.slideEnd]);

  const save = async (patch) => {
    try {
      await onSave(patch);
      setStatus({ text: t('workshopOrchestrator.saved') });
    } catch (err) {
      setStatus({ text: t('workshopOrchestrator.saveFailed', { error: err.message }), error: true });
    }
  };

  const saveNumber = (field, key) => () => {
    const value = toInt(draft[key]);
    const current = step[field] ?? null;
    if (value === current || Number.isNaN(value)) return;
    save({ [field]: value });
  };

  return (
    <Wrap>
      <Toggle type="button" $open={open} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {t('workshopOrchestrator.settingsTitle')}
        <ChevronDown size={16} />
      </Toggle>
      {open && (
        <>
          <Grid>
            <Field>
              {t('workshopOrchestrator.duration')}
              <input type="number" min="0" inputMode="numeric" disabled={locked} value={draft.duration}
                onChange={(e) => setDraft((d) => ({ ...d, duration: e.target.value }))}
                onBlur={saveNumber('suggestedDuration', 'duration')} />
            </Field>
            {hasDeck && (
              <>
                <Field>
                  {t('workshopOrchestrator.slideStart')}
                  <input type="number" min="1" inputMode="numeric" disabled={locked} value={draft.slideStart}
                    onChange={(e) => setDraft((d) => ({ ...d, slideStart: e.target.value }))}
                    onBlur={saveNumber('slideStart', 'slideStart')} />
                </Field>
                <Field>
                  {t('workshopOrchestrator.slideEnd')}
                  <input type="number" min="1" inputMode="numeric" disabled={locked} value={draft.slideEnd}
                    onChange={(e) => setDraft((d) => ({ ...d, slideEnd: e.target.value }))}
                    onBlur={saveNumber('slideEnd', 'slideEnd')} />
                </Field>
              </>
            )}
            <Field>
              {t('workshopOrchestrator.access')}
              <select disabled={locked} value={step.participantAccess}
                onChange={(e) => save({ participantAccess: e.target.value })}>
                <option value="auto">{t('workshopOrchestrator.accessAuto')}</option>
                <option value="open">{t('workshopOrchestrator.accessOpen')}</option>
                <option value="locked">{t('workshopOrchestrator.accessLocked')}</option>
              </select>
            </Field>
          </Grid>
          <Footer>
            <span>{t('workshopOrchestrator.screenStates', { count: step.screenStateCount ?? 0 })}</span>
            {onEditScreens && (
              <LinkButton type="button" onClick={onEditScreens}>
                <Monitor size={14} />
                {t('workshopOrchestrator.editScreens')}
              </LinkButton>
            )}
            {status && <Status $error={status.error}>{status.text}</Status>}
          </Footer>
        </>
      )}
    </Wrap>
  );
}
